import { t } from '@if/core/i18n'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ControlledNumberField,
  ControlledPercentField,
  ControlledPercentSlider,
  ControlledSelect,
  ErrorAlert,
  ErrorOverrides,
  FieldGrid,
  FormMethods,
  Help,
  StatCard,
  useDebouncedWatch,
  useFormDialog,
} from '@if/mui'
import { Box, Card, CardContent, CardHeader, Divider, MenuItem, Skeleton, Stack, Typography } from '@mui/material'
import ApprovalLikelihoodBar from '@src/components/ApprovalLikelihoodBar'
import { hasUserRight, useUser } from '@src/contexts/AppContext'
import {
  CreateCustomBuydownContentDto,
  PromoPlanDto,
  useCreateCustomBuydown,
  useGetCustomBuydownTermRates,
} from '@src/data/api/merchants-api/merchants-api'
import { conversionProbability, getApprovalLikelihood } from '@src/data/approvalLikelihood'
import { CUSTOM_BUYDOWN_FAMILY, EUserRights, LENDER_ADMIN_FEE_VALUES } from '@src/data/types/Constants'
import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getPromoPlanErrorCode, PromoPlanErrorCode } from './promoPlanErrorCodes'

const customBuydownErrorOverrides: ErrorOverrides = {
  unknown: (descriptor, t) => {
    if (getPromoPlanErrorCode(descriptor) === PromoPlanErrorCode.PlanAlreadyEnded) {
      return { role: 'contextual', message: t('merchant.customBuydown.errors.windowEditPlanAlreadyEnded') }
    }
    return undefined
  },
}

const DEFAULT_BORROWER_FEE_RATE = 0.06
const MERCHANT_FEE_RATE_MIN = 0.005
const MERCHANT_FEE_RATE_MAX = 0.3
const REBATE_MAX = 0.03
// Max financing amount cannot go below the smallest issuable loan.
const MIN_LOAN_AMOUNT = 500

const contributionMarks = [0, 5, 10, 15, 20, 25, 30].map((value) => ({ value, label: `${value}%` }))

const TERM_CARD_HEIGHT = 64
const TERM_SKELETON_COUNT = 5

const APPROVAL_LIKELIHOOD_HEIGHT = 76

const formatPercent = (value: number): string => `${(value * 100).toFixed(2)}%`
const buildPlanCode = (merchantFeeRate: number, borrowerFeeRate: number): string =>
  `FLASH${Math.round(merchantFeeRate * 10000)}/${borrowerFeeRate * 100}`

const getCustomBuydownFormSchema = (maxRebate: number) =>
  yup.object().shape({
    merchantFeeRate: yup
      .number()
      .min(MERCHANT_FEE_RATE_MIN, () =>
        t('merchant.customBuydown.errors.merchantFeeRateMin', { min: MERCHANT_FEE_RATE_MIN * 100 }),
      )
      .max(MERCHANT_FEE_RATE_MAX, () =>
        t('merchant.customBuydown.errors.merchantFeeRateMax', { max: MERCHANT_FEE_RATE_MAX * 100 }),
      )
      .required(),
    // Null means no maximum (unlimited).
    maxFinancingAmount: yup
      .number()
      .min(MIN_LOAN_AMOUNT, () => t('merchant.customBuydown.errors.maxFinancingAmountMin', { min: MIN_LOAN_AMOUNT }))
      .defined()
      .nullable(),
    // Lender admin fee: fixed choice of 0% / 3% / 6%, enforced by the selector's own options.
    borrowerFeeRate: yup.number().oneOf(LENDER_ADMIN_FEE_VALUES).required(),
    flashPlanRateRebate: yup.percent().min(0).max(maxRebate).required(),
  })

export type CustomBuydownFormValues = yup.InferSubmit<ReturnType<typeof getCustomBuydownFormSchema>>

const getDefaults = (plan: PromoPlanDto | null, storedRebate: number): CustomBuydownFormValues => ({
  merchantFeeRate: plan?.merchantFeeRate ?? MERCHANT_FEE_RATE_MIN,
  maxFinancingAmount: plan?.promoTermRates[0]?.maxFinancingAmount ?? null,
  borrowerFeeRate: plan?.borrowerFeeRate ?? DEFAULT_BORROWER_FEE_RATE,
  flashPlanRateRebate: storedRebate,
})

const toContentDto = (values: CustomBuydownFormValues): CreateCustomBuydownContentDto => ({
  family: CUSTOM_BUYDOWN_FAMILY,
  label: buildPlanCode(values.merchantFeeRate, values.borrowerFeeRate),
  promoCodePrefix: buildPlanCode(values.merchantFeeRate, values.borrowerFeeRate),
  merchantFeeRate: values.merchantFeeRate,
  borrowerFeeRate: values.borrowerFeeRate,
  maxFinancingAmount: values.maxFinancingAmount ?? null,
  supportsChargeback: false,
  flashPlanRateRebate: values.flashPlanRateRebate,
})

/**
 * Live per-term rate grid from the same backend calc the Save path freezes onto the plan.
 * The backend owns the term list; terms it filtered out (e.g. by the max-amount cap) are absent.
 */
const RatePreview = ({ merchantId, form }: { merchantId: string; form: FormMethods<CustomBuydownFormValues> }) => {
  const { t } = useTranslation()

  const debouncedFeeRate = useDebouncedWatch({ control: form.control, name: 'merchantFeeRate' })
  const debouncedMaxAmount = useDebouncedWatch({ control: form.control, name: 'maxFinancingAmount' })
  const debouncedRebate = useDebouncedWatch({ control: form.control, name: 'flashPlanRateRebate' })
  const borrowerFeeRate = useWatch({ control: form.control, name: 'borrowerFeeRate' })
  const { isValid } = form.formState
  const canPreview =
    isValid &&
    debouncedFeeRate >= MERCHANT_FEE_RATE_MIN &&
    debouncedFeeRate <= MERCHANT_FEE_RATE_MAX &&
    (debouncedMaxAmount === null || debouncedMaxAmount >= 0) &&
    debouncedRebate != null &&
    debouncedRebate >= 0 &&
    debouncedRebate <= REBATE_MAX
  const [previewRates, isLoading, error] = useGetCustomBuydownTermRates(
    merchantId,
    debouncedFeeRate,
    borrowerFeeRate,
    debouncedMaxAmount,
    debouncedRebate,
    canPreview,
  )

  // Show skeletons (not a text hint) until rates load, to avoid shifting the dialog height.
  const showSkeletons = !canPreview || (isLoading && !previewRates)

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('merchant.customBuydown.availableTerms')}
      </Typography>
      {canPreview && error ? (
        <ErrorAlert error={error} disabledGutterBottom />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1.5 }}>
          {showSkeletons
            ? Array.from({ length: TERM_SKELETON_COUNT }).map((_, index) => (
                <Skeleton key={index} variant="rounded" height={TERM_CARD_HEIGHT} />
              ))
            : previewRates?.map((rate) => (
                <StatCard
                  key={rate.termMonths}
                  size="sm"
                  label={`${rate.termMonths} ${t('common.months')}`}
                  value={formatPercent(rate.promoRate)}
                />
              ))}
        </Box>
      )}
    </Box>
  )
}

const SummaryFact = ({ label, value, caption }: { label: string; value: string; caption?: string }) => (
  <Box>
    <Typography variant="overline" color="primary" lineHeight={1.5} component="div">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" component="div">
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" component="div">
        {caption}
      </Typography>
    )}
  </Box>
)

const PlanCodeSummary = ({ form }: { form: FormMethods<CustomBuydownFormValues> }) => {
  const { t } = useTranslation()

  const { isValid } = form.formState
  const merchantFeeRate = useWatch({ control: form.control, name: 'merchantFeeRate' })
  const borrowerFeeRate = useWatch({ control: form.control, name: 'borrowerFeeRate' })

  return (
    <SummaryFact
      label={t('merchant.customBuydown.planCode')}
      value={isValid ? buildPlanCode(merchantFeeRate, borrowerFeeRate) : '—'}
    />
  )
}

const ApprovalLikelihoodSummary = ({ form }: { form: FormMethods<CustomBuydownFormValues> }) => {
  const { t } = useTranslation()

  const { isValid } = form.formState
  const merchantFeeRate = useWatch({ control: form.control, name: 'merchantFeeRate' })
  const level = isValid ? getApprovalLikelihood(merchantFeeRate) : null

  return (
    <Box sx={{ minHeight: APPROVAL_LIKELIHOOD_HEIGHT }}>
      {level && (
        <ApprovalLikelihoodBar
          label={t('merchant.customBuydown.customerApproval')}
          likelihood={level}
          probability={conversionProbability(merchantFeeRate)}
          hint={level !== 'veryHigh' ? t('merchant.customBuydown.customerApprovalHint') : undefined}
        />
      )}
    </Box>
  )
}

const EstimatedCostSummary = ({ form }: { form: FormMethods<CustomBuydownFormValues> }) => {
  const { t } = useTranslation()

  const { isValid } = form.formState
  const merchantFeeRate = useWatch({ control: form.control, name: 'merchantFeeRate' })
  const maxFinancingAmount = useWatch({ control: form.control, name: 'maxFinancingAmount' })

  if (maxFinancingAmount == null) {
    return null
  }

  return (
    <SummaryFact
      label={t('merchant.customBuydown.estimatedCost')}
      value={isValid ? formatCurrencyNoDecimals(merchantFeeRate * maxFinancingAmount) : '—'}
      caption={t('merchant.customBuydown.estimatedCostCaption', {
        amount: formatCurrencyNoDecimals(maxFinancingAmount),
      })}
    />
  )
}

export function useCustomBuydownDialog(merchantId: string, storedRebate: number) {
  const { t } = useTranslation()
  const [createCustomBuydown] = useCreateCustomBuydown()
  const userMaxRebate = useUser()?.buyDownRebateMax ?? 0
  const canEditRebate = userMaxRebate > 0
  const schema = useMemo(
    () => getCustomBuydownFormSchema(Math.max(userMaxRebate, storedRebate)),
    [userMaxRebate, storedRebate],
  )

  return useFormDialog<CustomBuydownFormValues, PromoPlanDto | null, PromoPlanDto>({
    title: {
      create: t('merchant.customBuydown.createTitle'),
      edit: t('merchant.customBuydown.editTitle'),
    },
    fullWidth: true,
    maxWidth: 'lg',
    defaultValues: (plan) => getDefaults(plan, storedRebate),
    schema,
    successMessage: (saved) => t('merchant.customBuydown.saved', { planCode: saved.promoCodePrefix || saved.label }),
    content: ({ form }) => (
      <Stack gap={2} divider={<Divider />}>
        <PlanCodeSummary form={form} />
        <Card variant="outlined">
          <CardHeader
            title={t('merchant.customBuydown.contributionTitle')}
            subheader={
              <Stack direction="row" alignItems="center" gap={0.5}>
                <span>{t('merchant.customBuydown.contributionSubtitle')}</span>
                <Help title={t('merchant.customBuydown.contributionHelp')} />
              </Stack>
            }
            action={
              <ControlledPercentField
                name="merchantFeeRate"
                control={form.control}
                label={t('merchant.customBuydown.contribution')}
                size="small"
                sx={{ width: 140 }}
              />
            }
            slotProps={{ action: { sx: { alignSelf: 'center', m: 0 } } }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Box sx={{ px: 1 }}>
              <ControlledPercentSlider
                name="merchantFeeRate"
                control={form.control}
                min={MERCHANT_FEE_RATE_MIN * 100}
                max={MERCHANT_FEE_RATE_MAX * 100}
                step={0.01}
                marks={contributionMarks}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <ApprovalLikelihoodSummary form={form} />
            </Box>
          </CardContent>
        </Card>
        <Stack gap={2}>
          <FieldGrid columns={{ xs: 1, sm: 3 }}>
            <ControlledNumberField
              name="maxFinancingAmount"
              control={form.control}
              label={t('merchant.customBuydown.maxFinancingAmount')}
              isMoney
              disableDecimals
            />
            <ControlledSelect
              name="borrowerFeeRate"
              control={form.control}
              label={
                <>
                  {t('merchant.customBuydown.adminFee')} <Help title={t('merchant.customBuydown.adminFeeTooltip')} />
                </>
              }
              parseValue={Number}
              disableEmptyOption
              disabled={!hasUserRight(EUserRights.canEditAdminFee)}
            >
              {LENDER_ADMIN_FEE_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {formatPercent(value)}
                </MenuItem>
              ))}
            </ControlledSelect>
            <ControlledPercentField
              name="flashPlanRateRebate"
              control={form.control}
              label={
                <>
                  {t('merchant.customBuydown.rebate')} <Help title={t('merchant.customBuydown.rebateTooltip')} />
                </>
              }
              helperText={t('merchant.customBuydown.rebateDescription')}
              disabled={!canEditRebate}
            />
          </FieldGrid>
          <EstimatedCostSummary form={form} />
        </Stack>
        <RatePreview merchantId={merchantId} form={form} />
      </Stack>
    ),
    onError: customBuydownErrorOverrides,
    onSubmit: async (values, { props: plan }) =>
      createCustomBuydown({
        merchantId,
        content: { ...toContentDto(values), versionTag: plan?.versionTag ?? null },
      }),
  })
}
